import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getToken } from "helpers/Tokens";
import { shouldLockToSubscriptionPlansForUser } from "helpers/RolesPermission";
import { isAiFeaturesEnabled } from "config/aiFeatureFlags";
import { isGhostSessionActive } from "context/platformAdminContext.utils";
import { parseBooleanVal } from "Utils/utils";
import { toast } from "react-toastify";
import { apiGetRequest, apiPostRequest } from "helpers/Requests";
import { updateAuthUser } from "store/actions/auth.actions";
import { getUser } from "store/selector/auth.selector";
import { URL_SP_AI_ENABLEMENT_NOTICE_DISMISS, URL_SP_AI_SETTINGS } from "store/services/URL";
import AiEnablementNoticeModal from "./AiEnablementNoticeModal";
import { buildAiEnablementFeatureList } from "./aiEnablementFeatures";
import {
  isAiEnablementNoticeDismissed,
  AI_ENABLEMENT_REMIND_LATER_KEY,
  isAiEnablementRemindLaterActive,
  isOrganisationAiEnabled,
  rememberAiEnablementNoticeDismissed,
  resolveAiEnablementSpId,
  shouldShowAiEnablementNotice,
  remindAiEnablementLater,
} from "./aiEnablementNoticeState";

const isAuthFlowBlockingNotice = () => {
  try {
    const step = localStorage.getItem("step");
    return Boolean(step && step !== "false");
  } catch {
    return false;
  }
};

const normalizePath = (pathname) => (pathname || "/").replace(/\/+$/, "") || "/";

// The portal landing page after login and OTP. Never on sign-in, OTP, onboarding, or other pages.
const DASHBOARD_PATHS = ["/", "/dashboard"];

const OTP_PATHS = new Set([
  "/select-otp",
  "/setup-otp",
  "/confirm-otp",
  "/verify-otp",
  "/verified-otp",
]);

const isOnDashboard = (pathname) => DASHBOARD_PATHS.includes(normalizePath(pathname));

const isOtpPath = (pathname) => OTP_PATHS.has(normalizePath(pathname));

const isPublicAuthPath = (pathname) => {
  const path = normalizePath(pathname);
  if (
    path === "/signup" ||
    path === "/forget-password" ||
    path === "/reset-password" ||
    path === "/confirmpassword" ||
    path === "/member-invitation"
  ) {
    return true;
  }
  return (
    path.startsWith("/sp-invitation") ||
    path.startsWith("/email-verification-code") ||
    path.startsWith("/view-template")
  );
};

// Same lock as UseRoutes: trial expired or payment failed, so only the plans screen is shown.
const isPaymentScreenOnly = (user) =>
  Boolean(user) &&
  !parseBooleanVal(user?.payment_setting?.wildcard) &&
  shouldLockToSubscriptionPlansForUser(user);

const AiEnablementNotice = () => {
  // SignIn puts the user in Redux before it stores the access token, so the token
  // check below also guards against a tokenless settings request (a 401 logs the user out).
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  // localStorage is the source of truth; this only re-renders after it changes.
  const [, setStorageVersion] = useState(0);
  const [dismissing, setDismissing] = useState(false);
  const [includedFeatures, setIncludedFeatures] = useState([]);
  const [featuresLoading, setFeaturesLoading] = useState(true);
  const spId = resolveAiEnablementSpId(user);

  // Another open tab chose Remind me later, or logged out.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === null || event.key === AI_ENABLEMENT_REMIND_LATER_KEY) {
        setStorageVersion((version) => version + 1);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const show = shouldShowAiEnablementNotice({
    aiFeaturesEnabled: isAiFeaturesEnabled,
    user,
    spId,
    aiEnabled: isOrganisationAiEnabled(user),
    noticeDismissed: isAiEnablementNoticeDismissed(user),
    remindLater: isAiEnablementRemindLaterActive(),
    blocked:
      !isOnDashboard(pathname) ||
      !getToken() ||
      isGhostSessionActive() ||
      isAuthFlowBlockingNotice() ||
      isOtpPath(pathname) ||
      isPublicAuthPath(pathname) ||
      isPaymentScreenOnly(user),
  });

  useEffect(() => {
    if (!show) {
      return undefined;
    }

    if (!getToken()) {
      return undefined;
    }

    let cancelled = false;
    setFeaturesLoading(true);

    (async () => {
      try {
        const res = await apiGetRequest(URL_SP_AI_SETTINGS);
        const payload = res?.data?.data ?? res?.data;
        if (cancelled) {
          return;
        }
        setIncludedFeatures(
          buildAiEnablementFeatureList(payload?.schema, payload?.ai_features)
        );
      } catch {
        if (!cancelled) {
          setIncludedFeatures([]);
        }
      } finally {
        if (!cancelled) {
          setFeaturesLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [show, spId]);

  if (!show) return null;

  const handleRemindLater = () => {
    remindAiEnablementLater();
    setStorageVersion((version) => version + 1);
  };

  const handleOpenAiSettings = () => {
    handleRemindLater();
    navigate("/settings/ai-settings");
  };

  const handleGotIt = async () => {
    if (dismissing) return;
    setDismissing(true);
    try {
      await apiPostRequest(URL_SP_AI_ENABLEMENT_NOTICE_DISMISS, {});
      dispatch(updateAuthUser(rememberAiEnablementNoticeDismissed(user)));
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Could not save your choice. Try again."
      );
    } finally {
      setDismissing(false);
    }
  };

  return (
    <AiEnablementNoticeModal
      isOpen
      features={includedFeatures}
      featuresLoading={featuresLoading}
      onRemindLater={handleRemindLater}
      onGotIt={handleGotIt}
      onOpenAiSettings={handleOpenAiSettings}
      dismissing={dismissing}
    />
  );
};

export default AiEnablementNotice;
